import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  Deposit,
  DepositCreateIn,
  DepositReceiptOutcome,
} from "../../../../../api/deposits";
import { newUuid } from "../../../../../api/idempotency";
import { useCreateDeposit, useIssueDepositReceipt } from "../../../../../hooks/useDeposits";
import {
  depositReceiptStateOf,
  type DepositReceiptState,
} from "../../../../../lib/depositReceipt";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";

/**
 * Състоянието на модала след опит:
 *  • `draft` — нищо не е тръгнало (или BE е отказал с 4xx — пак същото
 *    намерение, същият `requestId`);
 *  • `noAnswer` — `POST /deposits` без отговор (или 5xx): депозитът и бонът
 *    може вече да са записани. Следващият натиск праща СЪЩИЯ `requestId` —
 *    BE връща записания депозит и неговия бон, не прави втори;
 *  • `saved` — депозитът е записан, бонът още не е готов (`receiptState`):
 *    следващият натиск е „Издай бон" за ТОЗИ депозит
 *    (`POST /deposits/{id}/receipt`) — BE решава дали със същия ключ.
 * Готов бон (`printed`/`not_required`) не остава тук — `onReady` затваря.
 */
export type DepositSavePhase = "draft" | "noAnswer" | "saved";

/** Ключът на етикета на основния бутон по състоянието. */
export const depositSaveLabelKey = (
  phase: DepositSavePhase,
  receiptState: DepositReceiptState | null,
): string => {
  if (phase === "noAnswer") return "depositLoad.checkDeposit";
  if (phase === "draft") return "depositLoad.save";
  if (receiptState === "unknown") return "depositLoad.checkReceipt";
  if (receiptState === "card_charged") return "depositLoad.finishReceipt";
  return "depositLoad.issueAgain";
};

/** Категоричен отказ на BE (4xx) — заявката е прочетена и нищо не е пратено. */
const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

const isRefusal = (status: number | undefined) =>
  status !== undefined && status >= 400 && status < 500;

/**
 * 402 — терминалът доказа, че картата НЕ е изтеглена: пари не са взети и BE
 * е изтрил депозита. Следващият натиск е ново вземане със същия `requestId`
 * (ключът е свободен), не „Издай бон" за депозит, който вече го няма.
 */
const CARD_NOT_CHARGED_STATUS = 402;

/**
 * Записът на „Депозити" и касовият бон към него (Бон 1) — машината на
 * изходите по шаблона на свободната фактура (`invoicing/useInvoiceSubmit.ts`),
 * само че депозитът се записва ПРЕДИ бона: неуспял бон не губи депозита, а
 * повторът издава бона на вече записания (`saved`).
 */
export function useDepositSave({ onReady }: { onReady: (deposit: Deposit) => void }) {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const createMutation = useCreateDeposit();
  const issueMutation = useIssueDepositReceipt();

  // Един ключ за отварянето: повторен запис (загубен отговор) не прави втори
  // депозит. `newUuid` (не голото `crypto.randomUUID`) работи и по http.
  const requestIdRef = useRef("");
  const [noAnswer, setNoAnswer] = useState(false);
  const [savedDeposit, setSavedDeposit] = useState<Deposit | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const isPending = createMutation.isPending || issueMutation.isPending;
  const receiptState = savedDeposit ? depositReceiptStateOf(savedDeposit) : null;
  const phase: DepositSavePhase = savedDeposit ? "saved" : noAnswer ? "noAnswer" : "draft";

  /** Ново отваряне на модала — ново намерение. */
  const reset = useCallback(() => {
    requestIdRef.current = newUuid();
    setNoAnswer(false);
    setSavedDeposit(null);
    setReceiptError(null);
  }, []);

  const handleOutcome = (outcome: DepositReceiptOutcome) => {
    const { deposit } = outcome;
    setNoAnswer(false);
    const params = {
      id: deposit.id,
      name: deposit.name,
      amount: formatCents(parseMoneyToCents(deposit.amount) ?? 0),
      error: outcome.fiscal_result?.error || t("depositReceipt.noDetails"),
    };
    const state = depositReceiptStateOf(deposit);
    if (state === "ready") {
      message.success(
        deposit.fiscalStatus === "not_required"
          ? t("depositLoad.savedWithoutReceipt", params)
          : t("depositLoad.savedWithReceipt", params),
      );
      onReady(deposit);
      return;
    }
    setSavedDeposit(deposit);
    setReceiptError(outcome.fiscal_result?.error ?? null);
    if (state === "unknown") message.warning(t("depositLoad.receiptUnknown", params));
    else message.error(t("depositLoad.receiptFailed", params));
  };

  const submit = async (input: Omit<DepositCreateIn, "requestId">) => {
    if (isPending) return;
    const depositForReceipt = savedDeposit;
    try {
      const outcome = depositForReceipt
        ? await issueMutation.mutateAsync({
            depositId: depositForReceipt.id,
            paymentMethodId: depositForReceipt.paymentMethodId,
          })
        : await createMutation.mutateAsync({ ...input, requestId: requestIdRef.current });
      handleOutcome(outcome);
    } catch (error) {
      const status = httpStatusOf(error);
      if (depositForReceipt) {
        if (status === CARD_NOT_CHARGED_STATUS) {
          // Депозитът е изтрит (картата не е теглена) — обратно към формата.
          setSavedDeposit(null);
          setReceiptError(null);
          message.error(getApiErrorMessage(error, t("depositReceipt.failedGeneric")));
          return;
        }
        // 4xx — отказ преди принтера (принтерът на предишния опит го няма,
        // бонът още се печата, касата без способността): текстът е на BE.
        if (isRefusal(status)) {
          message.error(getApiErrorMessage(error, t("depositReceipt.failedGeneric")));
        } else {
          // Без отговор — бонът може да е излязъл: следващият опит е като
          // при неясен изход (BE пази ключа на бона).
          setSavedDeposit({ ...depositForReceipt, fiscalStatus: "unknown" });
          setReceiptError(null);
          message.warning(t("depositReceipt.noAnswer", { name: depositForReceipt.name }));
        }
        return;
      }
      // 4xx — отказ ПРЕДИ записа (право, метод, сума) или 402 (картата не е
      // теглена, депозитът е изтрит): нищо не е записано, а `requestId`
      // остава — същото намерение, още неизпълнено.
      if (isRefusal(status)) {
        message.error(getApiErrorMessage(error, t("depositLoad.saveError")));
        return;
      }
      setNoAnswer(true);
      message.warning(t("depositLoad.noAnswer"));
    }
  };

  return { submit, reset, isPending, phase, receiptState, receiptError, savedDeposit };
}
